/**
 * Tier Gate Hook
 *
 * Provides tier-aware access checks that combine:
 * 1. Subscription tier (ceiling) — is the org's plan high enough?
 * 2. Module toggles (admin preference) — has the admin enabled this?
 *
 * Usage:
 *   const { hasAccess, reason } = useTierGate("accounting");
 *   const { hasFeature } = useFeatureGate("solomon_ai");
 */

import { useBillingStatus, useForgeEntitlement } from "@/hooks/usePlatformBilling";
import { useCapability } from "@/hooks/useCapability";
import {
  tierMeetsMinimum,
  MODULE_CONFIG,
  FEATURE_GATES,
  TIER_DISPLAY,
  type ToggleableModule,
  type SubscriptionTier,
} from "@/lib/tier-config";

// ── Module Gate ──────────────────────────────────────────────

export interface TierGateResult {
  /** Whether the module is accessible (tier qualifies AND toggle is on) */
  hasAccess: boolean;
  /** Current org subscription tier */
  currentTier: SubscriptionTier;
  /** The minimum tier required for this module */
  requiredTier: SubscriptionTier;
  /** The Forge entitlement read failed; unrelated Stripe subscription errors do not deny module access. */
  isError?: boolean;
  /** Whether the tier alone qualifies (ignoring toggle) */
  tierQualifies: boolean;
  /** Whether the admin toggle is on (only meaningful if tier qualifies) */
  toggleEnabled: boolean;
  /** Human-readable reason if access is denied */
  reason: string | null;
  /** Whether billing data is still loading */
  isLoading: boolean;
  /** The display name of the required tier for upgrade prompts */
  requiredTierName: string;
}

export function useTierGate(module: ToggleableModule): TierGateResult {
  // Delegate access resolution to the capability resolver. For the ported
  // modules the capability key equals the ToggleableModule id, and the
  // resolver's reason strings are byte-identical to the original gate's.
  const { available, blockedBy, reason, isLoading } = useCapability(module);
  const { effectiveTier } = useBillingStatus();
  const { error: entitlementError } = useForgeEntitlement();

  const currentTier = (effectiveTier || "free") as SubscriptionTier;
  const requiredTier = MODULE_CONFIG[module].minimumTier;
  // For ported modules `role` never fires (no rbac field), but guard it too so
  // a tier-blocked check stays a pure tier signal.
  const tierQualifies = blockedBy !== "tier" && blockedBy !== "role";
  // Only meaningful when tierQualifies is true: when tier-blocked the resolver short-circuits before evaluating the toggle, so this reports true even if the admin toggle is off. Consumers read it only behind a `tierQualifies && !toggleEnabled` guard.
  const toggleEnabled = blockedBy !== "toggle";
  return {
    hasAccess: available,
    currentTier,
    requiredTier,
    tierQualifies,
    toggleEnabled,
    reason: reason ?? null,
    isLoading,
    isError: entitlementError != null,
    requiredTierName: TIER_DISPLAY[requiredTier].name,
  };
}

// ── Feature Gate (non-module) ────────────────────────────────

export interface FeatureGateResult {
  hasFeature: boolean;
  currentTier: SubscriptionTier;
  requiredTier: SubscriptionTier;
  reason: string | null;
  isLoading: boolean;
  /**
   * True when the underlying entitlement query failed (e.g. a transient
   * PostgREST/network error) rather than genuinely resolving to "no
   * entitlement". `hasFeature` still fails closed to `false` in this case
   * (billing status is unknown, not confirmed-absent) — callers that need
   * to distinguish "confirmed not entitled" from "couldn't check" (e.g. to
   * avoid denying a paid user over a retryable failure) should check this
   * before treating `hasFeature: false` as authoritative.
   */
  isError: boolean;
  requiredTierName: string;
}

export function useFeatureGate(featureKey: string): FeatureGateResult {
  const { effectiveTier, isLoading, error } = useBillingStatus();
  const isError = error != null;

  const currentTier = (effectiveTier || "free") as SubscriptionTier;
  const config = FEATURE_GATES[featureKey];

  if (!config) {
    return {
      hasFeature: true, // Unknown features are unrestricted
      currentTier,
      requiredTier: "free",
      reason: null,
      isLoading,
      isError,
      requiredTierName: "Free",
    };
  }

  const requiredTier = config.minimumTier;
  const hasFeature = tierMeetsMinimum(currentTier, requiredTier);

  let reason: string | null = null;
  if (!hasFeature) {
    const tierInfo = TIER_DISPLAY[requiredTier];
    reason = `${config.label} requires the ${tierInfo.name} plan ($${tierInfo.monthlyPrice}/mo)`;
  }

  return {
    hasFeature,
    currentTier,
    requiredTier,
    reason,
    isLoading,
    isError,
    requiredTierName: TIER_DISPLAY[requiredTier].name,
  };
}
